import { fetchResolvedMediaBlob } from './mediaFetch'
import { createNadDebug } from '../utils/debugLog'

const mediaCacheLog = createNadDebug('media-readiness')

const MAX_CACHE_ENTRIES = 16
const MAX_CACHE_BYTES = 128 * 1024 * 1024

type CacheState = 'loading' | 'ready'

interface CacheEntry {
  key: string
  state: CacheState
  refs: number
  lastAccess: number
  byteSize: number
  controller: AbortController
  promise: Promise<CachedMediaBlob> | null
  blobUrl: string | null
  /** Unversioned URLs coalesce only while leased; they are unsafe to retain across mounts. */
  retainAfterRelease: boolean
}

export interface CachedMediaBlob {
  blob: Blob
  blobUrl: string
}

export interface MediaBlobLease {
  /** Resolves to the shared blob and its cache-owned object URL. */
  promise: Promise<CachedMediaBlob>
  /** Idempotent. A pending request aborts only after its final lease releases. */
  release: () => void
}

export interface AcquireMediaBlobOptions {
  projectId: string
  sourcePath: string
  resolvedUrl: string
}

const entries = new Map<string, CacheEntry>()
let cachedBytes = 0
let accessClock = 0

function safeSourceIdentity(sourcePath: string): string {
  try {
    const parsed = new URL(sourcePath, 'http://dreamwrite.local')
    if (parsed.origin === 'http://dreamwrite.local') return parsed.pathname
    // Absolute uploaded/signed assets are identified without query credentials.
    return `${parsed.origin}${parsed.pathname}`
  } catch {
    return sourcePath.split('?')[0]
  }
}

export function mediaBlobRevisionOf(resolvedUrl: string): string | null {
  try {
    return new URL(resolvedUrl, 'http://dreamwrite.local').searchParams.get('v')
  } catch {
    return null
  }
}

/**
 * Stable, credential-free media identity. The project scopes relative paths and
 * the node's cache-bust revision prevents stale bytes after an asset update.
 */
export function mediaBlobCacheKey({
  projectId,
  sourcePath,
  resolvedUrl,
}: AcquireMediaBlobOptions): string {
  return `${projectId}\u0000${safeSourceIdentity(sourcePath)}\u0000${mediaBlobRevisionOf(resolvedUrl) ?? ''}`
}

function revokeEntry(entry: CacheEntry): void {
  if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl)
  cachedBytes = Math.max(0, cachedBytes - entry.byteSize)
  entries.delete(entry.key)
}

function evictUnreferencedEntries(): void {
  if (entries.size <= MAX_CACHE_ENTRIES && cachedBytes <= MAX_CACHE_BYTES) return

  const candidates = [...entries.values()]
    .filter((entry) => entry.state === 'ready' && entry.refs === 0)
    .sort((a, b) => a.lastAccess - b.lastAccess)

  for (const entry of candidates) {
    if (entries.size <= MAX_CACHE_ENTRIES && cachedBytes <= MAX_CACHE_BYTES) break
    revokeEntry(entry)
  }
}

/**
 * Acquire one shared media request. Waveform decoders and playback preloaders
 * use the same project/path/revision identity, so concurrent consumers join the
 * same fetch and safely share its cache-owned object URL.
 */
export function acquireMediaBlob(options: AcquireMediaBlobOptions): MediaBlobLease {
  const key = mediaBlobCacheKey(options)
  let entry = entries.get(key)

  if (!entry) {
    const controller = new AbortController()
    const created: CacheEntry = {
      key,
      state: 'loading',
      refs: 0,
      lastAccess: ++accessClock,
      byteSize: 0,
      controller,
      promise: null,
      blobUrl: null,
      retainAfterRelease: mediaBlobRevisionOf(options.resolvedUrl) !== null,
    }

    created.promise = fetchResolvedMediaBlob(options.resolvedUrl, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Media fetch failed: HTTP ${response.status}`)
        return response.blob()
      })
      .then((blob) => {
        // A last-lease release can remove and abort an entry while the response is
        // settling. Never resurrect it or allocate an orphan object URL.
        if (entries.get(key) !== created) throw new DOMException('Media load was released', 'AbortError')
        const blobUrl = URL.createObjectURL(blob)
        created.state = 'ready'
        created.blobUrl = blobUrl
        created.byteSize = blob.size
        created.lastAccess = ++accessClock
        cachedBytes += blob.size
        mediaCacheLog({
          event: 'media-ready',
          source_key: key,
          bytes: blob.size,
        })
        evictUnreferencedEntries()
        return { blob, blobUrl }
      })
      .catch((error) => {
        if (entries.get(key) === created) entries.delete(key)
        if (created.blobUrl) URL.revokeObjectURL(created.blobUrl)
        mediaCacheLog({
          event: 'media-failed',
          source_key: key,
          reason: error instanceof Error ? error.message : String(error),
        })
        throw error
      })

    entry = created
    entries.set(key, entry)
    mediaCacheLog({ event: 'media-load-start', source_key: key })
  } else {
    entry.lastAccess = ++accessClock
    mediaCacheLog({
      event: entry.state === 'loading' ? 'media-cache-joined' : 'media-cache-hit',
      source_key: key,
    })
  }

  const promise = entry.promise
  if (!promise) throw new Error(`Media cache invariant violated for ${key}`)
  const leasedEntry = entry
  leasedEntry.refs += 1
  let released = false
  return {
    promise,
    release: () => {
      if (released) return
      released = true
      leasedEntry.refs = Math.max(0, leasedEntry.refs - 1)
      leasedEntry.lastAccess = ++accessClock
      if (leasedEntry.state === 'loading' && leasedEntry.refs === 0) {
        entries.delete(leasedEntry.key)
        leasedEntry.controller.abort()
        return
      }
      if (leasedEntry.state === 'ready' && leasedEntry.refs === 0 && !leasedEntry.retainAfterRelease) {
        revokeEntry(leasedEntry)
        return
      }
      evictUnreferencedEntries()
    },
  }
}

/** Test-only reset; exported so cache lifetime and URL revocation stay verifiable. */
export function resetMediaBlobCacheForTests(): void {
  for (const entry of entries.values()) {
    entry.controller.abort()
    if (entry.blobUrl) URL.revokeObjectURL(entry.blobUrl)
  }
  entries.clear()
  cachedBytes = 0
  accessClock = 0
}

export function mediaBlobCacheStatsForTests(): {
  entries: number
  bytes: number
  refs: number
} {
  return {
    entries: entries.size,
    bytes: cachedBytes,
    refs: [...entries.values()].reduce((sum, entry) => sum + entry.refs, 0),
  }
}
